import * as React from 'react';

import {Button} from 'shells/button/button';
import {TextButtonWithConfirmation} from 'shells/button/text-button-with-confirmation/text-button-with-confirmation';
import {ButtonGroup} from 'shells/button-group/button-group';
import {Link} from 'shells/link/link';
import {PaginatedTable} from 'shells/table/paginated-table';
import {convertSortByToUrlSortParam} from 'shells/table/table-sort-helpers';

import type {MeetingFragment} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import type {ReportURLParams} from '../../reports/report-types';
import {convertSortParamsToSortBy, convertSortStringToSortParams} from '../../reports/report-utils';

import {useCancelBooking, useDeleteBooking, useGetBookings} from '../graphql/hooks';
import {DEFAULT_PAGE_SIZE, SortOrder} from '../graphql/hooks/use-get-bookings';

import {BookingsTableEmptyState} from './bookings-table-empty-state';
import {CancelButton} from './cancel-button';
import {FormSubmission} from './form-submission';
import {getIsScheduled, getStatusBadgeInfo} from './helpers';

type Props = {
    isEmbedded: boolean;
    meeting: MeetingFragment;
    urlParams: ReportURLParams<'meeting'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
};

export function BookingsTable(props: Props) {
    const {meeting, isEmbedded, onParamChange} = props;
    const meetingId = meeting.id;
    const meetingType = meeting.meetingType;

    const sortBy =
        props.urlParams && props.urlParams.sort
            ? convertSortParamsToSortBy(props.urlParams.sort)
            : null;

    const {
        bookings,
        numBookings,
        isLoading,
        fetchNextPage,
        fetchPreviousPage,
        fetchFirstPageWithNewSort,
    } = useGetBookings(
        {
            meetingId,
        },
        sortBy ? convertSortByToUrlSortParam(sortBy) : null
    );
    const cancelBooking = useCancelBooking();
    const deleteBooking = useDeleteBooking();
    const onCancelBooking = React.useCallback(
        (bookingId, optionalMessage) => {
            cancelBooking({bookingId, meetingId, optionalMessage});
        },
        [cancelBooking, meetingId]
    );
    const onDeleteBooking = React.useCallback(
        (bookingId) => {
            deleteBooking({bookingId, meetingId});
        },
        [deleteBooking, meetingId]
    );

    const columns = React.useMemo(() => {
        const columnsArray = [
            {
                id: 'name',
                Header: 'Meeting',
                // @ts-expect-error
                accessor: (row) => row.activity,
                dataType: 'component',
                hasLeftBorder: true,
                width: 200,
                Cell: ({cell}: any) =>
                    isEmbedded ? (
                        <div className='truncate' title={cell.value.name}>
                            {cell.value.name}
                        </div>
                    ) : (
                        <Button
                            variant='text-primary'
                            onClick={() => {
                                appHistory.replace({
                                    ...appHistory.location,
                                    hash: `/${cell.value.id}`,
                                    state: {modal: true},
                                });
                            }}
                            noPadding={true}
                            className='full-width'
                        >
                            <div className='truncate' title={cell.value.name}>
                                {cell.value.name}
                            </div>
                        </Button>
                    ),
                disableSortBy: true,
            },
            {
                id: 'startTime',
                Header: 'Date',
                // @ts-expect-error
                accessor: (row) => row.startTime,
                dataType: 'timestamp',
                width: 100,
                maxWidth: 100, // override default maxWidth
                minWidth: 100, // override default minWidth
            },
            {
                id: 'status',
                Header: 'Status',
                // @ts-expect-error
                accessor: (row) => getStatusBadgeInfo(row.status),
                dataType: 'statusBadge',
                width: 100,
                maxWidth: 100, // override default maxWidth
                minWidth: 100, // override default minWidth
                disableSortBy: true,
            },
            {
                id: 'bookedBy',
                Header: 'Booked by',
                // @ts-expect-error
                accessor: (row) => (row.formSubmission ? row.formSubmission.primaryEntity : null),
                dataType: 'linkedEntity',
                disableSortBy: true,
            },
            {
                id: 'createdTime',
                Header: 'Date booked',
                // @ts-expect-error
                accessor: (row) => row.createdTime,
                dataType: 'timestamp',
                width: 100,
                maxWidth: 100, // override default maxWidth
                minWidth: 100, // override default minWidth
            },
            {
                id: 'formSubmission',
                Header: 'Form submission',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                width: 100,
                disableSortBy: true,
                Cell: ({cell}: any) => (
                    <FormSubmission formSubmission={cell.value.formSubmission} />
                ),
            },
            {
                id: 'buttons',
                Header: '',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                minWidth: 200,
                width: 200,
                hasRightBorder: true,
                disableSortBy: true,
                Cell: ({cell}: any) => {
                    if (getIsScheduled(cell.value.status)) {
                        return (
                            <ButtonGroup>
                                <Link
                                    newTab={true}
                                    href={cell.value.rescheduleUrl}
                                    variant='button-primary'
                                    size='small'
                                >
                                    Reschedule
                                </Link>
                                <CancelButton
                                    eventName={cell.value.activity.name}
                                    startTime={cell.value.startTime}
                                    bookedByName={cell.value.formSubmission.primaryEntity.name}
                                    onCancelBooking={(optionalMessage?: string | null) =>
                                        onCancelBooking(cell.value.id, optionalMessage)
                                    }
                                />
                            </ButtonGroup>
                        );
                    } else {
                        return (
                            <TextButtonWithConfirmation
                                onConfirm={() => onDeleteBooking(cell.value.id)}
                                question='Are you sure you’d like to remove this booking? Once removed, it will no longer appear in this table.'
                            />
                        );
                    }
                },
            },
        ];

        if (meetingType === 'ROUND_ROBIN') {
            // @ts-expect-error
            columnsArray.splice(4, 0, {
                id: 'bookedWith',
                Header: 'Booked with',
                accessor: (row) => row.hostUser,
                dataType: 'linkedEntity',
            });
        }

        return columnsArray;
    }, [onCancelBooking, onDeleteBooking, isEmbedded, meetingType]);

    React.useEffect(() => {
        if (!sortBy) {
            const sortParams = convertSortStringToSortParams(SortOrder.DATE_BOOKED_DESC);
            onParamChange({sort: sortParams}, true);
        }
    }, [sortBy, onParamChange]);

    const updateUrlParamsOnSortChange = (newSortString: string) => {
        const sortParams = convertSortStringToSortParams(newSortString);
        props.onParamChange({sort: sortParams}, true);
    };

    return (
        <PaginatedTable
            // @ts-expect-error
            columns={columns}
            data={bookings}
            isLoading={isLoading}
            fullWidth={true}
            sortBy={sortBy || undefined}
            updateUrlParamsOnSortChange={updateUrlParamsOnSortChange}
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={fetchPreviousPage}
            fetchFirstPageWithNewSort={fetchFirstPageWithNewSort}
            pageSize={DEFAULT_PAGE_SIZE}
            totalCount={numBookings}
            emptyState={<BookingsTableEmptyState />}
        />
    );
}
